// Components
import Box from "@mui/material/Box";
import {
  BoldArrowHorizontal,
  BoldArrowVertical,
  BoldCaption,
  BoldHeadAbove,
  BoldHeadFront,
  HEAD_CENTRE_X,
  INK,
  WARM,
} from "./boldHead";

const PUPIL_SHIFT = 3;
const EYE_OFFSET_ABOVE = 15;
const EYE_Y_ABOVE = 73;
const EYE_OFFSET_FRONT = 13;
const EYE_Y_FRONT = 52;

type HorizontalVergenceFigureType = {
  // "in" is base out, which pulls the eyes together; "out" is base in, which lets them relax.
  kind: "in" | "out";
  caption: string;
};

/**
 * What the prism is doing to both eyes, seen from above.
 *
 * Seen from above, so it is NOT mirrored. See `BoldHeadAbove`.
 */
export const HorizontalVergenceFigure = ({
  kind,
  caption,
}: HorizontalVergenceFigureType) => (
  <Box
    component="svg"
    role="img"
    data-testid="synoptophore-vergence-figure"
    aria-label={caption}
    viewBox="0 34 120 100"
    sx={{ display: "block", width: "100%", mx: "auto" }}
  >
    <BoldHeadAbove />

    {[-1, 1].map((side) => {
      const eyeX = HEAD_CENTRE_X + side * EYE_OFFSET_ABOVE;
      // Base out turns each eye towards the nose; base in turns it away.
      const towards = kind === "in" ? -side : side;

      return (
        <g key={side}>
          <circle
            cx={eyeX}
            cy={EYE_Y_ABOVE}
            r={8}
            fill="#FFFFFF"
            stroke={WARM}
            strokeWidth={2.6}
          />
          <circle
            cx={eyeX + towards * PUPIL_SHIFT}
            cy={EYE_Y_ABOVE - 2}
            r={3.4}
            fill={INK}
          />
          <BoldArrowHorizontal
            y={44}
            from={eyeX - towards * 4}
            to={eyeX + towards * 14}
          />
        </g>
      );
    })}

    <BoldCaption text={caption} />
  </Box>
);

/**
 * The vertical range: one eye pushed up and the other down.
 *
 * The head faces the reader, so this figure is a MIRROR. Nothing here names an individual eye,
 * so it costs nothing either way -- but it is drawn with the same head as the tilt figure, and
 * that one does.
 */
export const VerticalVergenceFigure = ({ caption }: { caption: string }) => (
  <Box
    component="svg"
    role="img"
    data-testid="synoptophore-vertical-vergence-figure"
    aria-label={caption}
    viewBox="0 18 120 112"
    sx={{ display: "block", width: "100%", mx: "auto" }}
  >
    <BoldHeadFront />

    {[
      { side: -1, up: true },
      { side: 1, up: false },
    ].map(({ side, up }) => {
      const eyeX = HEAD_CENTRE_X + side * EYE_OFFSET_FRONT;

      return (
        <g key={side}>
          <ellipse
            cx={eyeX}
            cy={EYE_Y_FRONT}
            rx={9.5}
            ry={6.8}
            fill="#FFFFFF"
            stroke={WARM}
            strokeWidth={2.4}
          />
          <circle
            cx={eyeX}
            cy={EYE_Y_FRONT + (up ? -4 : 4)}
            r={3.1}
            fill={INK}
          />
          {up ? (
            <BoldArrowVertical x={eyeX} from={40} to={29} />
          ) : (
            <BoldArrowVertical x={eyeX} from={64} to={75} />
          )}
        </g>
      );
    })}

    <BoldCaption text={caption} />
  </Box>
);
